import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMoreVertical, HiFilter } from 'react-icons/fi';

// Can return undefined, string or . .
export function guessByPossibleElement(element) {
  if (element === undefined || !React.isValidElement(element) || typeof element === 'string') {
    return element;
  }

  const { children: childrenList } = element.props;

  if (typeof childrenList === 'string') {
    return childrenList;
  }

  const possibleLabels = [];

  if (Array.isArray(childrenList)) {
    childrenList.forEach((subElement) => {
      if (typeof subElement === 'string') {
        possibleLabels.push(subElement);
      } else if (subElement && subElement.props && subElement.props.children) {
        const childText = guessByPossibleElement(subElement.props.children); // this.
        if (childText) {
          possibleLabels.push(childText);
        }
      }
    });
  }

  return possibleLabels.join(' ');
}

export function filterForAriaProps(props = {}) {
  const ariaProps = {};
  Object.keys(props).forEach((key) => {
    if (key.startsWith('aria-')) {
      ariaProps[key] = props[key];
    }
  });
  return ariaProps;
}

export function guessByChildText(children) {
  if (!children) {
    return null;
  }
  if (typeof children === 'string') {
    return children;
  }
  if (typeof children?.props?.children === 'string') {
    return children?.props?.children;
  }
  // We might recurse, but would possibly want limits
  // if (typeof children?.props?.children === 'object') {
  //   return guessByChildText(children?.props?.children);
  // }
  if (children?.length > 0) {
    if (children.length === 1) {
      return guessByChildText(children[0]);
    }
    if (children.length > 1) {
      return children.map((child) => guessByChildText(child)).join(' ');
    }
  }

  if (children?.props?.children?.length > 0) {
    const childrenList = children.props.children;
    if (childrenList.length > 0) {
      if (childrenList.length === 1) {
        return guessByChildText(childrenList[0]);
      }
      if (childrenList.length > 1) {
        return childrenList.map((child) => guessByChildText(child)).join(' ');
      }
    }
  }
  return null;
}

// Could make a version that checks for exact elements like Label
export function guessTextFromPossibleElement(reactElement) {
  // shoudl only send elements here but just in case . .
  if (typeof reactElement === 'string') {
    return reactElement;
  }

  if (!React.isValidElement(reactElement)) {
    return null;
  }

  const { props } = reactElement;

  // Check if name is a string
  if (typeof props?.name === 'string') {
    return props.name;
  }

  // Fallback to guessing text from children
  return guessByChildText(props.children);
}

export function guessTextFromLabel(label) {
  // shoudl only send elements here but just in case . .
  if (typeof label === 'string') {
    return label;
  }

  if (!React.isValidElement(label)) {
    return null;
  }

  const { props } = label;

  // Check if label is a string
  if (typeof props?.label === 'string') {
    return props.label;
  }

  // Check if label is an object with a 'get' method
  if (props?.label?.get) {
    return props.label.get('name');
  }

  // Check if name is a string
  if (typeof props?.name === 'string') {
    return props.name;
  }

  // Fallback to guessing text from children
  return guessByChildText(props.children);
}

export const guessTextOnIcon = (icon) => {
  switch (icon) {
    case FiMoreVertical:
      return 'Open Menu';
    case HiFilter:
      return 'Filter';
    case 'chevron-left':
      return 'left'; // or close Previous
    case 'chevron-right':
      return 'right'; // or next
    case 'chevron-up':
      return 'up'; // close or collapse
    case 'chevron-down':
      return 'down';
    case 'caret-left':
      return 'left'; // usually Previous
    case 'caret-right':
      return 'right'; // usually Next
    case 'caret-down':
      return 'close';
    case 'caret-up':
      return 'open';
    case 'cross':
      return 'remove';
    default:
      if (typeof icon === 'string') {
        return icon;
      }
      if (icon?.props?.children) {
        return guessByChildText(icon.props.children);
      }
      return '';
  }
};

export const formAriaProps = (propsOrRest, otherProps = {}, options) => {
  options = {
    passOnExistingAria: true,
    enableTabIndexNotDisabled: false,
    explicitTabIndex: undefined,
    ...options
  };
  let ariaProps = {};
  const {
    ariaChecked,
    checked,
    ariaDisabled,
    disabled,
    ariaExpanded,
    expanded,
    ariaHelpTextId,
    helperTextId,
    ariaHaspopup,
    haspopup,
    ariaHidden,
    hidden,
    ariaLabel,
    label,
    ariaLabelId,
    labelId,
    ariaReadOnly,
    readOnly,
    ariaRequired,
    required,
    ariaSelected,
    selected,
    ariaLive,
    ariaDescribedby,
    ariaDescription,
    tableLabelId,
    name
  } = {
    ...propsOrRest,
    // deliberately let this object override the rest
    ...otherProps
  };

  // according to specs, aria-labelledby will always take precedence over aria-label if both are applied to the same element.
  if (ariaLabelId || labelId || tableLabelId) {
    ariaProps['aria-labelledby'] = ariaLabelId || labelId || tableLabelId;
  } else if (ariaLabel || label || name) {
    ariaProps['aria-label'] = ariaLabel || label || name;
  }

  if (otherProps.name && !propsOrRest.name) {
    ariaProps.name = otherProps.name;
  }

  if (ariaDescribedby || ariaHelpTextId || helperTextId) {
    ariaProps['aria-describedby'] = ariaDescribedby || ariaHelpTextId || helperTextId;
  }

  if (ariaDescription) {
    ariaProps['aria-description'] = ariaDescription;
  }

  // What if we want tabIndex on a disabled element?
  if (ariaDisabled === true || disabled === true) {
    ariaProps['aria-disabled'] = ariaDisabled || disabled;
  } else if (options.enableTabIndexNotDisabled) {
    if (options.explicitTabIndex !== undefined) {
      ariaProps.tabIndex = options.explicitTabIndex;
    } else {
      ariaProps.tabIndex = 0;
    }
  }

  if (ariaExpanded !== undefined || expanded !== undefined) {
    ariaProps['aria-expanded'] = ariaExpanded ?? expanded;
  }

  if (ariaHaspopup !== undefined || haspopup !== undefined) {
    ariaProps['aria-haspopup'] = ariaHaspopup ?? haspopup;
  }

  if (ariaRequired === true || required === true) {
    ariaProps['aria-required'] = true;
  }

  if (ariaReadOnly === true || readOnly === true) {
    ariaProps['aria-readonly'] = ariaReadOnly || readOnly;
  }

  if (ariaSelected !== undefined || selected !== undefined) {
    ariaProps['aria-selected'] = ariaSelected ?? selected;
  }

  if (ariaLive !== undefined) {
    ariaProps['aria-live'] = ariaLive;
  }

  if (ariaChecked !== undefined || checked !== undefined) {
    ariaProps['aria-checked'] = ariaChecked ?? checked;
  }

  if (ariaHidden === true || hidden === true) {
    ariaProps['aria-hidden'] = ariaHidden || hidden;
  }

  // CAREFUL - so original props with arai-XX will override any of the above
  if (options.passOnExistingAria) {
    ariaProps = {
      ...ariaProps,
      ...filterForAriaProps(propsOrRest)
    };
  }

  return ariaProps;
};

export function useFocusTrigger(defaultRef) {
  const ref = useRef(null);

  // If a default ref is provided, use it as the initial value
  if (defaultRef && typeof defaultRef === 'object' && 'current' in defaultRef) {
    ref.current = defaultRef.current;
  }
  /*
    scenarios worth considering but for now assume functions using useFocusTrigger are only functional, etc, so we can assume useRef style . .
    BUT could we some day pass from many layers of components?
    else if (defaultRef && typeof defaultRef === 'function') {
      // If it's a function, call it to get the initial value
      ref.current = defaultRef();
    } else if (defaultRef) {
      // Otherwise, just set it directly
      ref.current = defaultRef;
    }
    */

  const triggerFocus = useCallback(() => {
    if (ref.current && typeof ref.current.focus === 'function') {
      ref.current.focus();
    }
  }, []);

  return [ref, triggerFocus];
}

const DEFAULT_SELECTOR = '[role="tab"]';
/**
 * Hook to make Blueprint's date picker components accessible by fixing tabindex on grid cells
 * @param {Object} options - Configuration options
 * @returns {Object} - Ref to attach to the container element
 */
export const useTabable = (options = { selector: DEFAULT_SELECTOR }) => {
  const containerRef = useRef(null);
  const { selector } = options;

  useEffect(() => {
    const mutationObserver = new MutationObserver(() => {
      // Find all grid cells with tabindex -1 and make them keyboard navigable
      const gridCells = containerRef?.current?.querySelectorAll(`${selector}[tabindex="-1"]`);
      if (containerRef && gridCells?.length) {
        for (let i = 0; i < gridCells.length; i++) {
          gridCells[i]?.setAttribute('tabindex', '0');
        }
      } else if (containerRef && selector === DEFAULT_SELECTOR) {
        const plainQuery = containerRef.current?.querySelectorAll(`${selector}`);

        if (!plainQuery?.length) {
          console.warn(
            `## using DEFAULT_SELECTOR: ${DEFAULT_SELECTOR} in useTabable with no results.  You may need a different selector.`,
            containerRef.current
          );
        }
      }
    });

    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }

    return () => mutationObserver.disconnect();
  }, [selector]);

  return containerRef;
};

// This may not work in some cases if a blueprint component literally blocks event bubbling . . . which they do on occasion
// Please treat the response from this custom hook as a hint, not a guarantee
export const useFocusSource = () => {
  const [lastInteractionType, setLastInteractionType] = useState('mouse'); // Default assumption
  const [isFocusFromKeyboard, setIsFocusFromKeyboard] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        setLastInteractionType('keyboard');
      }
    };

    const handleMouseDown = () => {
      setLastInteractionType('mouse');
    };

    const handleFocus = () => {
      // If the last interaction was keyboard, and this isn't a click-triggered focus
      if (lastInteractionType === 'keyboard') {
        setIsFocusFromKeyboard(true);
      } else {
        setIsFocusFromKeyboard(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focusin', handleFocus);
    };
  }, [lastInteractionType]);

  return isFocusFromKeyboard;
};

/**
 * Hook to focus the first item in a list of elements
 * @param {Object} options - Configuration options
 * @param {string} options.selector - CSS selector for the items to focus
 * @param {string} [options.containerSelector] - Optional container selector (defaults to document)
 * @param {number} [options.delay=0] - Optional delay in ms before focusing
 * @param {boolean} [options.onlyWhenEmpty=false] - Only focus if no item is already focused
 * @returns {Object} - Methods and refs for handling focus
 */
export function useFocusFirstItem({ selector, containerSelector, delay = 0, onlyWhenEmpty = false }) {
  const containerRef = useRef(null);

  const focusFirstItem = useCallback(() => {
    const focusItem = () => {
      // Determine the container to search within
      const container = containerSelector
        ? document.querySelector(containerSelector)
        : containerRef.current || document;

      if (!container) {
        return;
      }

      // Find all focusable items
      const items = container.querySelectorAll(selector);

      if (items.length === 0) {
        return;
      }

      // Check if we should only focus when no item is already focused
      if (onlyWhenEmpty) {
        const { activeElement } = document;
        const alreadyFocused = Array.from(items).some((item) => item === activeElement || item.contains(activeElement));

        if (alreadyFocused) {
          return;
        }
      }

      // Focus the first item
      items[0].focus();
    };

    // Apply delay if specified
    if (delay > 0) {
      setTimeout(focusItem, delay);
    } else {
      focusItem();
    }
  }, [selector, containerSelector, delay, onlyWhenEmpty]);

  return {
    focusFirstItem,
    containerRef
  };
}

/**
 * Creates a function to focus the first item in a list of elements
 * @param {Object} options - Configuration options
 * @param {string} options.selector - CSS selector for the items to focus
 * @param {string|Element} [options.container] - Container element or selector (defaults to document)
 * @param {number} [options.delay=0] - Optional delay in ms before focusing
 * @param {boolean} [options.onlyWhenEmpty=false] - Only focus if no item is already focused
 * @returns {Function} - Function to focus the first item
 */
export function createFocusFirstItem({ selector, container = document, delay = 0, onlyWhenEmpty = false }) {
  return function focusFirstItem() {
    const focusItem = () => {
      // Determine the container to search within
      let containerElement = container;

      // If container is a string (selector), find the actual element
      if (typeof container === 'string') {
        containerElement = document.querySelector(container);
      }

      if (!containerElement) {
        return;
      }
      // Find all focusable items
      const items = containerElement.querySelectorAll(selector);

      if (items.length === 0) {
        return;
      }
      // Check if we should only focus when no item is already focused
      if (onlyWhenEmpty) {
        const { activeElement } = document;
        const alreadyFocused = Array.from(items).some((item) => item === activeElement || item.contains(activeElement));

        if (alreadyFocused) {
          return;
        }
      }

      // Focus the first item
      items[0].focus();
    };

    // Apply delay if specified
    if (delay > 0) {
      return setTimeout(focusItem, delay);
    }
    focusItem();
    return null;
  };
}

/**
 * Helper to bind a focus function to a class instance
 * @param {Object} component - Class component instance (this)
 * @param {Object} options - Focus options
 * @returns {Function} - Bound focus function
 */
export function bindFocusFirstItem(component, options) {
  const focusFn = createFocusFirstItem(options);
  return focusFn.bind(component);
}
